import { useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import blinkLogo from "@/assets/blink-logo-white.svg";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";

export function LoginPage() {
  const { isAuthenticated, isLoading, login } = useAuth();
  const { inProgress } = useMsal();
  const msalBusy = inProgress !== InteractionStatus.None;
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const sessionReason = searchParams.get("reason");

  const from = (location.state as { from?: { pathname: string } } | null)?.from
    ?.pathname;

  useEffect(() => {
    // Wait until MSAL has finished handleRedirectPromise before acting —
    // calling loginRedirect while inProgress !== None throws interaction_in_progress.
    if (msalBusy) return;
    if (isAuthenticated && !isLoading) {
      // After MSAL redirect completes, user lands back on /login — send them home.
      // Prefer the original destination stored in router state; fall back to /.
      navigate(from ?? "/", { replace: true });
    } else if (!isAuthenticated && !isLoading && !sessionReason) {
      // Auto-initiate Microsoft login, preserving the original destination so
      // MSAL returns the user directly there after authentication.
      void login(from);
    }
  }, [isAuthenticated, isLoading, msalBusy]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="w-full max-w-sm space-y-8 px-4 text-center">
        {/* Session expired notice */}
        {sessionReason && (
          <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950 px-4 py-3 text-sm text-amber-800 dark:text-amber-300">
            {sessionReason === "timeout"
              ? "You were signed out due to inactivity. Please sign in again."
              : "Your session has expired. Please sign in again."}
          </div>
        )}

        {/* Logo */}
        <div className="flex flex-col items-center gap-3">
          <div className="flex h-16 w-24 items-center justify-center rounded-2xl bg-primary shadow-lg">
            <img src={blinkLogo} alt="Blink" className="h-16 w-24" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Relay</h1>
            <p className="text-sm text-muted-foreground">Tech Request Portal</p>
          </div>
        </div>

        {/* Card */}
        <div className="rounded-xl border bg-card p-6 shadow-sm space-y-4">
          <div>
            <h2 className="text-lg font-semibold">Sign in to continue</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Use your Blink Network Microsoft account
            </p>
          </div>

          <Button
            onClick={() => void login(from)}
            className="w-full gap-2"
            disabled={isLoading || msalBusy}
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                Redirecting…
              </span>
            ) : (
              <>
                <MicrosoftIcon />
                Sign in with Microsoft
              </>
            )}
          </Button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-muted" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">Or</span>
            </div>
          </div>

          <Button
            variant="outline"
            className="w-full gap-2"
            onClick={() => {
              const from = (
                location.state as { from?: { pathname: string } } | null
              )?.from?.pathname;
              // Pass `from` via router state so EmailLoginPage can embed it in the magic-link.
              // Also keep sessionStorage as a fallback for same-tab flows.
              if (from && from !== "/login") {
                sessionStorage.setItem("auth_redirect", from);
              }
              navigate("/auth/email/request", { state: { from } });
            }}
          >
            Sign in with Email
          </Button>
        </div>

        <p className="text-xs text-muted-foreground">
          For access issues contact{" "}
          <a
            href="mailto:it@blinkcharging.com"
            className="text-primary hover:underline"
          >
            it@blinkcharging.com
          </a>
        </p>
      </div>
    </div>
  );
}

function MicrosoftIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 21 21"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}
